import { useMutation, useQuery } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";

import ExportActiveReport from "../../../components/Modals/ExportActiveReport";
import { FETCH_COUNTRIES } from "../../../graphql/query/Country";
import {
  FETCH_SHIPMENT as fetchShipment,
  FETCH_SHIPMENTS_LIST as fetchShipmentsList,
} from "../../../graphql/query/Shipment";
import {
  Breadcrumb,
  Button,
  MenuButton,
  Modal,
  ResponsivePrimaryNav,
  SearchResultNavigation,
} from "../../../components/ui";
import useSearchResultNavigation from "../../../hooks/useSearchResultNavigation";
import { useSearchSynopsis } from "../../../hooks/useSearchSynopsis";

import routes from "../pages/Wizard/routes";
import { buildShipmentNavigationTargetUrl } from "../searchResultNavigation";
import {
  CONFIRM_SHIPMENT_ITEMS as confirmShipmentItemsMutation,
  CREATE_SHIPMENT_JOB as createShipmentJobMutation,
  CREATE_SHIPMENT_RETURN as createShipmentReturnMutation,
  REMOVE_SHIPMENT_ITEMS as removeShipmentItemsMutation,
} from "../../../graphql/mutation/Shipment";

import ShipmentSearch from "./ShipmentSearch";
import { useState } from "react";
import CreateOnwardShipmentBody from "./CreateOnwardShipmentBody";
import AddShipment from "./AddShipment";
import exportModule from "./Actions/ExportModule";
import AdhocReportBody from "./Actions/AdhocReportBody";
import {
  shipmentFilterStore,
  shipmentNavigationStore,
} from "./useShipmentFilters";
import { clearSelectionState } from "../../../util/clearSelectionState";
import {
  getSearchAwareLabel,
  markModuleSearchSubmitted,
  useModuleSearchSubmitted,
} from "../../../util/usePersistedFilterFormik";
import { scopeBulkActionTargets } from "../../../util/scopeBulkActionTargets";

const SHIPMENT_LIST_CONTEXT = {};

const PrimaryNav = props => {
  const { formik } = props;
  const location = useLocation();
  const isSearchSubmitted = useModuleSearchSubmitted(location.pathname);
  const navigate = useNavigate();
  const search = shipmentFilterStore.get();
  const pathnames = location?.pathname.split("/");
  const selected = Object.keys(formik?.values?.selected?.ids || {});
  const ids = selected?.map(key => {
    const obj = formik?.values?.selected?.ids?.[key];
    return obj?.item?.id || obj?.id;
  });
  const select = formik?.values?.selected?.select;
  const disabled = ids?.length === 0;
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const id = pathnames?.length > 2 ? pathnames[2] : null;
  const { data: { countries = [] } = {} } = useQuery(FETCH_COUNTRIES);

  const { data: { shipment } = {} } = useQuery(
    fetchShipment({
      shipmentDescriptions: true,
      unconfirmedItemCount: true,
    }),
    {
      skip: !id,
      variables: { id: +id },
    },
  );

  const [
    createShipmentJob,
    {
      data: { createShipmentJob: { error: createShipmentJobError } = {} } = {},
      loading: createShipmentJobLoading,
      reset: resetCreateShipmentJob,
    },
  ] = useMutation(createShipmentJobMutation());

  const [
    confirmShipmentItems,
    {
      data: {
        confirmShipmentItems: { error: confirmShipmentItemsError } = {},
      } = {},
      loading: confirmShipmentItemsLoading,
      reset,
    },
  ] = useMutation(confirmShipmentItemsMutation(), {
    refetchQueries: ["FetchWarehouseMoveV2"],
  });

  const [
    removeShipmentWarehouseItems,
    {
      data: {
        removeShipmentWarehouseItems: { error: removeShipmentItemsError } = {},
      } = {},
      loading: removeShipmentItemsLoading,
      reset: resetRemove,
    },
  ] = useMutation(removeShipmentItemsMutation(), {
    refetchQueries: ["FetchWarehouseMoveV2"],
  });

  const [
    createShipmentReturn,
    {
      data: {
        createShipmentReturn: { error: createShipmentReturnError } = {},
      } = {},
      loading: createShipmentReturnLoading,
      reset: returnReset,
    },
  ] = useMutation(createShipmentReturnMutation());

  const path = pathnames[pathnames?.length - 1];
  const route = routes?.find(item => {
    if (item?.href == path) {
      return item;
    } else if (item?.href?.includes(path)) {
      return item;
    }
  });
  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams.get("status") || "all";
  const buildShipmentDetailsUrl = (recordId, activeSubPage = "details") =>
    buildShipmentNavigationTargetUrl({
      activeSubPage,
      recordId,
      status,
    });
  const {
    hasNavigationContext,
    hasNext,
    hasPrevious,
    navigationLoading,
    navigateToNext,
    navigateToPrevious,
    showNavigationControls,
  } = useSearchResultNavigation({
    activeSubPage: route?.href || "details",
    buildTargetUrl: buildShipmentNavigationTargetUrl,
    dataKey: "shipmentsV2",
    fetchQuery: fetchShipmentsList,
    listContext: SHIPMENT_LIST_CONTEXT,
    navigate,
    navigationStore: shipmentNavigationStore,
    recordId: id ? Number(id) : null,
    status,
  });

  const isShowingSearchResults = isSearchSubmitted && status === "all";
  const { synopsis: searchSynopsis } = useSearchSynopsis(
    shipmentNavigationStore,
  );
  const label = getSearchAwareLabel("All Shipments", isShowingSearchResults);
  const rootLabel =
    isShowingSearchResults && searchSynopsis
      ? { label, title: `Shipment Search ${searchSynopsis}` }
      : label;

  const steps = [
    {
      label: rootLabel,
      onClick: () =>
        navigate(
          status === "all" ? "/shipments" : `/shipments?status=${status}`,
        ),
    },
    ...(shipment
      ? [
          {
            label: `Shipment ${shipment?.id}`,
            onClick: () => navigate(buildShipmentDetailsUrl(shipment.id)),
          },
          {
            label: route?.name,
            onClick: () =>
              navigate(
                buildShipmentDetailsUrl(shipment.id, route?.href || "details"),
              ),
          },
        ]
      : []),
  ];

  const breadcrumbProps = {
    currentStepIndex: steps?.length - 1,
    onChange: index => steps[index]?.onClick(),
    steps: steps?.map(item => item?.label),
  };

  const confirmCreateJobProps = {
    title: createShipmentJobError ? "Error" : "Create Job",
    scale: "sm",
    description: createShipmentJobError
      ? createShipmentJobError
      : "Are you sure you wish to create job for confirmed shipment items?",
    closeOnBackdrop: true,
    renderBody: ({ closeModal }) => {
      const handleConfirm = () => {
        createShipmentJob({
          variables: {
            id: shipment?.id,
          },
          update: (cache, { data }) => {
            const { createShipmentJob: { job, success } = {} } = data || {};
            if (success) {
              closeModal();
              navigate(`/jobs/${job.id}/details`);
            }
          },
        });
      };

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label={createShipmentJobError ? "Close" : "Cancel"}
            action={createShipmentJobError ? "primary" : "default"}
            onClick={() => {
              resetCreateShipmentJob();
              closeModal?.();
            }}
          />
          {!createShipmentJobError && (
            <Button
              label={createShipmentJobLoading ? "Creating" : "create"}
              disabled={createShipmentJobLoading}
              onClick={handleConfirm}
            />
          )}
        </div>
      );
    },
  };

  const confirmShipmentItemsErrorMessage =
    typeof confirmShipmentItemsError === "string"
      ? confirmShipmentItemsError
      : confirmShipmentItemsError?.message;
  const confirmShipmentItemsProps = {
    title: confirmShipmentItemsErrorMessage
      ? "Error"
      : "Confirm Shipment Items",
    scale: "sm",
    description: confirmShipmentItemsErrorMessage
      ? confirmShipmentItemsErrorMessage
      : "Are you sure you wish to confirm all unconfirmed shipment items? This cannot be undone.",
    closeOnBackdrop: true,
    renderBody: ({ closeModal }) => {
      const handleConfirm = () => {
        confirmShipmentItems({
          variables: {
            input: { id: shipment?.id },
          },
          update: (cache, { data }) => {
            const { confirmShipmentItems: { success } = {} } = data || {};
            if (success) {
              closeModal();
            }
          },
        });
      };

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label={confirmShipmentItemsErrorMessage ? "Close" : "Cancel"}
            action={confirmShipmentItemsErrorMessage ? "primary" : "default"}
            onClick={() => {
              reset();
              closeModal?.();
            }}
          />
          {!confirmShipmentItemsErrorMessage && (
            <Button
              label={confirmShipmentItemsLoading ? "Confirming" : "Confirm"}
              disabled={confirmShipmentItemsLoading}
              onClick={handleConfirm}
            />
          )}
        </div>
      );
    },
  };

  const removeShipmentItemsProps = {
    title: removeShipmentItemsError ? "Error" : "Remove Shipment Items",
    scale: "sm",
    description: removeShipmentItemsError
      ? removeShipmentItemsError
      : "Are you sure you want to remove all items?",
    closeOnBackdrop: true,
    renderBody: ({ closeModal }) => {
      const handleRemove = () => {
        removeShipmentWarehouseItems({
          variables: {
            id: shipment?.id,
          },
          update: (cache, { data }) => {
            const { removeShipmentWarehouseItems: { success } = {} } =
              data || {};
            if (success) {
              closeModal();
            }
          },
        });
      };

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label={removeShipmentItemsError ? "Close" : "Cancel"}
            action={removeShipmentItemsError ? "primary" : "default"}
            onClick={() => {
              resetRemove();
              closeModal?.();
            }}
          />
          {!removeShipmentItemsError && (
            <Button
              label={removeShipmentItemsLoading ? "Removing" : "Remove"}
              disabled={removeShipmentItemsLoading}
              onClick={handleRemove}
            />
          )}
        </div>
      );
    },
  };

  const createOnwardShipmentProps = {
    title: undefined,
    scale: "md",
    closeOnBackdrop: false,
    description: undefined,
    renderBody: ({ closeModal }) => (
      <CreateOnwardShipmentBody
        closeModal={closeModal}
        shipmentId={shipment?.id}
        countries={countries}
        navigate={navigate}
      />
    ),
  };

  const createReturnShipmentProps = {
    title: createShipmentReturnError ? "Error" : "Create Shipment Return",
    scale: "sm",
    description: createShipmentReturnError
      ? createShipmentReturnError
      : "Are you sure you wish to create return for this shipment?",
    closeOnBackdrop: true,
    renderBody: ({ closeModal }) => {
      const handleReturn = () => {
        createShipmentReturn({
          variables: {
            id: shipment?.id,
          },
          update: (cache, { data }) => {
            const { createShipmentReturn: { newShipmentId, success } = {} } =
              data || {};
            if (success) {
              closeModal();
              navigate(`/shipments/${newShipmentId}/details`);
            }
          },
        });
      };

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label={createShipmentReturnError ? "Close" : "Cancel"}
            action={createShipmentReturnError ? "primary" : "default"}
            onClick={() => {
              returnReset();
              closeModal?.();
            }}
          />
          {!createShipmentReturnError && (
            <Button
              label={createShipmentReturnLoading ? "Confirming" : "Confirm"}
              disabled={createShipmentReturnLoading}
              onClick={handleReturn}
            />
          )}
        </div>
      );
    },
  };

  const copyDescriptionProps = {
    title: "Copy Description?",
    scale: "sm",
    description: "Do you wish to copy the Shipment description?",
    closeOnBackdrop: true,
    renderBody: ({ closeModal }) => {
      const handleCopyDescription = () => {
        navigator.clipboard.writeText(shipment?.shipmentDescriptions);
        closeModal();
      };

      return (
        <div className="flex flex-col">
          <div className="py-2">
            <p className="text-sm">
              {!shipment?.shipmentDescriptions
                ? "No Description Available"
                : ""}
            </p>
          </div>
          <div className="mt-4 flex">
            <Button
              className="mr-3"
              label={"Cancel"}
              action={"default"}
              onClick={() => {
                closeModal?.();
              }}
            />
            <Button
              disabled={!shipment?.shipmentDescriptions}
              label={"Copy description"}
              onClick={handleCopyDescription}
            />
          </div>
        </div>
      );
    },
  };

  // Detail pages share Formik with the list view, so bulk-action props must
  // be scoped to the URL id; otherwise list-side state leaks into actions
  // here. See scopeBulkActionTargets.
  const scopedTargets = scopeBulkActionTargets({
    entityId: id,
    selectedIds: ids,
    fallbacks: { search, select },
  });

  const defaultActionProps = {
    closeOnBackdrop: false,
    hideCloseButton: true,
    ids: scopedTargets.ids,
    onClose: () => clearSelectionState(formik),
    scale: "md",
    search: scopedTargets.search,
    select: scopedTargets.select,
  };

  const [isExportReportOpen, setIsExportReportOpen] = useState(false);
  const actionMenuProps = {
    label: "Actions",
    options: [
      {
        label: "New Shipment",
        modalProps: {
          body: AddShipment,
          closeOnBackdrop: false,
          hideCloseButton: true,
          scale: "lg",
        },
      },
      {
        disabled,
        label: "Create an Adhoc Report",
        modalProps: {
          body: AdhocReportBody,
          ...defaultActionProps,
          exportModule,
        },
      },
      {
        disabled: !shipment?.id && disabled,
        label: "Export Shipment Report",
        modalProps: {
          ...defaultActionProps,
          body: ExportActiveReport,
          optimise: true,
          type: "Shipment",
          ids: shipment?.id ? [shipment?.id] : ids,
          isExportReportOpen,
          onOpen: () => {
            setIsExportReportOpen(true);
          },
          onClose: () => {
            setIsExportReportOpen(false);
          },
        },
      },
      ...(shipment?.id
        ? [
            {
              label: "Create Job",
              modalProps: confirmCreateJobProps,
            },
            {
              disabled: shipment?.unconfirmedItemCount === 0,
              label: "Confirm Shipment Items",
              title:
                shipment?.unconfirmedItemCount === 0
                  ? "There are no unconfirmed shipment items"
                  : undefined,
              modalProps: confirmShipmentItemsProps,
            },
            {
              label: "Remove Shipment Items",
              modalProps: removeShipmentItemsProps,
            },
            {
              label: "Create Onward",
              modalProps: createOnwardShipmentProps,
            },
            {
              label: "Create Return",
              modalProps: createReturnShipmentProps,
            },
            {
              label: "Copy Description",
              modalProps: copyDescriptionProps,
            },
          ]
        : []),
    ],
  };

  const searchModalProps = {
    body: ShipmentSearch,
    closeOnBackdrop: true,
    scale: "fullscreen",
    isSearchOpen,
    onClose: () => setIsSearchOpen(false),
    onSearchSubmitted: () => {
      clearSelectionState(formik);
      markModuleSearchSubmitted(location.pathname);
    },
  };

  return (
    <ResponsivePrimaryNav>
      <div>
        <Breadcrumb {...breadcrumbProps} />
      </div>
      <div className="flex items-center gap-2 sm:gap-4 lg:justify-end">
        <div id="form-save-status" />
        <SearchResultNavigation
          hasNavigationContext={hasNavigationContext}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
          navigationLoading={navigationLoading}
          navigateToNext={navigateToNext}
          navigateToPrevious={navigateToPrevious}
          showNavigationControls={showNavigationControls}
        />
        <Modal {...searchModalProps}>
          <Button
            label="Shipment Search"
            action="black"
            onClick={() => setIsSearchOpen(true)}
          />
        </Modal>
        <MenuButton {...actionMenuProps} commandPalette />
      </div>
    </ResponsivePrimaryNav>
  );
};

export default PrimaryNav;
